













































































































import { Component, Mixins, Prop } from 'vue-property-decorator';
import { ExternalWallet } from '~/mixins';
import erc20Abi from '~/data/abi/erc20.json';
import erc1155Abi from '~/data/abi/erc1155.json';
import erc721Abi from '~/data/abi/erc721.json';
import ExclamationTriangle from '~/components/SvgIcons/ExclamationTriangle.vue';
import { SelectedUserItem } from '~/types/batch-bridge';

enum DisplayState {
  DETAILS = 'DETAILS',
  ACCEPT = 'ACCEPT',
  DECLINE = 'DECLINE',
}

enum AllowanceRequestType {
  STORE_PAYMENT = 'STORE_PAYMENT',
  CLAIM_FEE_PAYMENT = 'CLAIM_FEE_PAYMENT',
  GYRI_BRIDGE = 'GYRI_BRIDGE',
  EXCHANGE = 'EXCHANGE',
}

enum ContractType {
  ERC20 = 'ERC20',
  ERC721 = 'ERC721',
  ERC1155 = 'ERC1155',
}

@Component({
  components: { ExclamationTriangle },
})
export default class GiveAllowance extends Mixins(ExternalWallet) {
  @Prop(String) readonly token!: string;
  @Prop(String) readonly image!: string;
  @Prop(String) readonly contractAddress!: string;
  @Prop(String) readonly requestType!: AllowanceRequestType;
  @Prop(String) readonly contractType!: ContractType;
  @Prop(Array) readonly selectedItems!: SelectedUserItem[];
  @Prop(Number) readonly minAmount!: number | string;
  @Prop(String) readonly addressToApprove!: string;

  DisplayState = DisplayState;
  displayState = DisplayState.DETAILS;
  // TODO: each time the user needs to grant an allowance, it requires gas--should we give them the option to give an "unlimited" amount as an allowance so they only have to do it once? Could be a security vulnerability.
  amount = +this.minAmount;
  txInFlight = false;

  requestDeniedText = '';
  allowanceExplanation = '';

  tokenIdFromSelectedItem(token: SelectedUserItem) {
    if (token.network === 'ETHEREUM') {
      if (token.fungible) {
        return token.ethereumBaseId;
      }
    }
    return undefined;
  }

  created() {
    switch (this.requestType) {
      case AllowanceRequestType.STORE_PAYMENT:
        this.requestDeniedText = this.$t(
          'components.wallet.giveAllowance.decline',
        ) as string;
      case AllowanceRequestType.EXCHANGE:
        this.requestDeniedText = this.$t(
          'components.wallet.giveAllowance.declineExchange',
        ) as string;
      case AllowanceRequestType.CLAIM_FEE_PAYMENT:
        this.requestDeniedText = this.$t(
          'components.wallet.giveAllowance.declineTokenClaim',
        ) as string;
        break;
      case AllowanceRequestType.GYRI_BRIDGE:
        this.requestDeniedText = this.$t(
          'components.wallet.giveAllowance.declineGyriBridge',
        ) as string;
        break;
    }

    switch (this.contractType) {
      case ContractType.ERC20:
        const formattedAmount = this.$cryptoFormat(this.minAmount);
        this.allowanceExplanation = this.$t(
          'components.wallet.giveAllowance.allowanceExplanation',
          {
            minAmount: formattedAmount,
            symbol:
              this.selectedItems && this.selectedItems[0]
                ? this.tokenIdFromSelectedItem(this.selectedItems[0])
                : this.token,
          },
        ) as string;
        break;
      case ContractType.ERC721:
      case ContractType.ERC1155:
        this.allowanceExplanation = this.$t(
          'components.wallet.giveAllowance.allowanceExplanationGeneral',
        ) as string;
        break;
    }
  }

  accept() {
    this.displayState = this.DisplayState.ACCEPT;
  }

  decline() {
    this.displayState = this.DisplayState.DECLINE;
  }

  goBack() {
    this.displayState = this.DisplayState.DETAILS;
  }

  async grantERC20Allowance() {
    const contract = this.getContractInstance(this.contractAddress, erc20Abi);
    const decimals = await contract.decimals();
    const amountInt = this.integerizeAmount(
      this.amount.toFixed(Math.min(decimals, 8)),
      decimals,
    );
    const amountString = amountInt.toString();

    // TODO: address security concern about the orderPaymentContractAddress -- could someone be malicious and slip their own address in place for it and steal everyone's money?
    return contract.approve(this.addressToApprove, amountString);
  }

  grantERC721Allowance() {
    const contract = this.getContractInstance(this.contractAddress, erc721Abi);

    return contract.setApprovalForAll(this.addressToApprove, true);
  }

  grantERC1155Allowance() {
    const contract = this.getContractInstance(this.contractAddress, erc1155Abi);

    return contract.setApprovalForAll(this.addressToApprove, true);
  }

  async grantAllowance() {
    if (this.txInFlight) {
      return;
    }

    let transaction;

    this.txInFlight = true;

    try {
      switch (this.contractType) {
        case ContractType.ERC20:
          transaction = await this.grantERC20Allowance();
          break;
        case ContractType.ERC721:
          transaction = await this.grantERC721Allowance();
          break;
        case ContractType.ERC1155:
          transaction = await this.grantERC1155Allowance();
          break;
      }

      const receipt = await transaction.wait();

      this.txInFlight = false;
      if (receipt) {
        this.handleGrantComplete({ success: true });
      }
    } catch (error) {
      this.$sentry.captureException(error);
      this.txInFlight = false;
      this.handleGrantComplete({ success: false });
    }
  }

  handleGrantComplete({ success }: { success: boolean }) {
    if (success) {
      this.$emit('success');
    } else {
      const errorMessage = 'something went wrong';
      this.$emit('error', errorMessage);
    }
  }
}
